import React, { useState } from 'react';
import {
  BookOpen,
  Shield,
  Sword,
  ExternalLink,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Flame,
  RefreshCw,
  Users,
  Landmark,
} from 'lucide-react';

const SONS_OF_THE_STANDARD_AMAZON_URL = 'https://www.amazon.co.uk/dp/B0GX2ZYLMY';
const sampleMoments = {
  bloodAndHonour: [
    {
      title: 'The Fall After Lugdunum',
      icon: Sword,
      what:
        'The civil war leaves defeated men exposed to vengeance, suspicion, and imperial cruelty. Varro survives the conflict, but survival becomes only the beginning of his punishment.',
      why:
        'Rome cannot allow the memory of rival loyalty to remain unbroken. Men who once served under another banner must be humiliated before they can be forgotten.',
      human:
        'Varro carries the shame of defeat as heavily as any wound. The world he served has collapsed, and the law he trusted now wears the face of punishment.',
      echo:
        'The battle ends, but defeat keeps walking beside the living.',
    },
    {
      title: 'Paulus Catena’s Judgement',
      icon: Landmark,
      what:
        'Paulus Catena turns prisoners, former soldiers, and the vulnerable into instruments of spectacle, profit, and fear.',
      why:
        'Catena understands that power does not only rule through law. It rules through display, debt, humiliation, and the careful destruction of hope.',
      human:
        'To Varro, Catena is not merely corrupt. He is the living proof that Rome can be hollowed out from within by men who love authority more than justice.',
      echo:
        'Some men do not need a battlefield to make war upon the helpless.',
    },
    {
      title: 'The Arena at Deva',
      icon: Flame,
      what:
        'Varro is forced into the arena beneath the gaze of Roman officials, local elites, and those who profit from public violence.',
      why:
        'The spectacle is meant to break more than the body. It is a warning to every defeated soldier who still remembers another loyalty.',
      human:
        'Varro has lost rank, freedom, and name, yet something within him refuses to kneel. The arena becomes the place where survival begins to turn into purpose.',
      echo:
        'A man may be chained before a crowd and still remain undefeated within himself.',
    },
    {
      title: 'The Physician’s Pact',
      icon: Users,
      what:
        'Nestor, a Greek physician serving within the Roman system, treats Varro’s wounds and begins to see the cruelty surrounding Paulus Catena.',
      why:
        'Healing becomes dangerous when the healer understands that the wounded are not criminals, but men deliberately ruined for power and profit.',
      human:
        'Nestor is caught between survival and conscience. His hands serve Rome, but his heart begins to move toward rebellion.',
      echo:
        'The hand that closes a wound may also open the path to defiance.',
    },
    {
      title: 'Brothers in Bondage',
      icon: Users,
      what:
        'The captive soldiers are trained, tested, and reduced in status, yet among them a new brotherhood begins to form.',
      why:
        'Rome intends isolation to weaken them. Instead, shared suffering becomes a rough and dangerous form of loyalty.',
      human:
        'Varro no longer thinks only as an officer. He begins to see the men beside him not as units to command, but as lives bound to his choices.',
      echo:
        'A chain can bind wrists. It can also remind men that they are not alone.',
    },
    {
      title: 'The Cornovii Watch',
      icon: Landmark,
      what:
        'Local people witness Roman justice performed as punishment and theatre, forcing them to weigh fear against anger.',
      why:
        'The arena is meant to discipline the province as much as the prisoners. Every spectator is asked to accept Rome’s version of order.',
      human:
        'Among the Cornovii, old resentments stir. Some see only danger in resistance; others begin to recognise the cost of obedience.',
      echo:
        'A crowd can be made to watch. It cannot always be made to believe.',
    },
    {
      title: 'The Emperor’s Champion',
      icon: Shield,
      what:
        'Varro is forced against a brutal champion whose fame has been built upon the deaths of condemned men.',
      why:
        'Catena needs a final demonstration: that even the strongest rebel can be consumed by Rome’s chosen killer.',
      human:
        'The fight becomes more than survival. Varro sees another captive of violence before him, a man shaped into a weapon and praised for it.',
      echo:
        'Even the strongest blade may long to stop being used.',
    },
    {
      title: 'The Refusal to Kill',
      icon: Shield,
      what:
        'At the moment of victory, Varro refuses the simple path of execution and chooses instead to turn the arena against itself.',
      why:
        'Killing the champion would satisfy the crowd. Sparing him challenges the whole machinery of spectacle.',
      human:
        'Varro discovers that mercy can be an act of rebellion when cruelty is expected as obedience.',
      echo:
        'Justice does not always raise the sword. Sometimes it lowers it.',
    },
    {
      title: 'Escape from Deva',
      icon: Flame,
      what:
        'The violence of the arena breaks outward as Varro and his allies seize the narrow chance to flee.',
      why:
        'No formal rescue can reach them. Freedom must be taken in confusion, through courage, sacrifice, and trust made under fire.',
      human:
        'Every step away from the amphitheatre costs someone something. Escape is not release from grief; it is the beginning of responsibility.',
      echo:
        'Freedom rarely opens like a gate. More often, it is torn through smoke.',
    },
    {
      title: 'The Ridgeway Paths',
      icon: Landmark,
      what:
        'Varro moves through the landscapes beyond Deva, seeking allies, survivors, and the scattered remnants of the Twentieth.',
      why:
        'The rebellion cannot be built in one place. It must be gathered from roads, hill paths, hidden camps, and old loyalties.',
      human:
        'The land becomes more than setting. It tests Varro, shelters him, and asks whether he can belong to something beyond Rome.',
      echo:
        'The old roads remember every army, but the small paths remember the hunted.',
    },
    {
      title: 'The Boar Raised',
      icon: Sword,
      what:
        'The surviving men of Legio XX gather around a changed purpose, no longer merely preserving the old legion but becoming something harder to define.',
      why:
        'The eagle belongs to imperial command. The boar speaks of endurance, local memory, and the stubborn life of men who refuse erasure.',
      human:
        'Varro must learn that leadership after betrayal cannot simply repeat the old forms. It must earn trust from those who have already lost too much.',
      echo:
        'Not every standard points back to empire. Some point forward through ruin.',
    },
    {
      title: 'The Reckoning at Deva',
      icon: Flame,
      what:
        'All paths bend back toward Deva, where vengeance, justice, and the fate of the surviving legion collide.',
      why:
        'Catena cannot be left as the shape of Roman rule. Varro must confront not only the man who ruined him, but the part of himself still hungry for revenge.',
      human:
        'The final reckoning tests whether Varro’s rebellion is merely wounded pride, or the beginning of a different moral order.',
      echo:
        'A man may win revenge and still lose himself. Justice asks for more.',
    },
  ],
  shadowsOfTheEagle: [
    {
      title: 'After the Eagle',
      icon: Shield,
      what:
        'The survivors of rebellion must live with what victory has cost them, while Rome begins to tighten its grip again.',
      why:
        'The end of one struggle does not bring peace. It leaves wounded people, exposed settlements, and unfinished questions of loyalty.',
      human:
        'Varro carries command like a scar. Those around him look for certainty, but he knows the old answers have failed.',
      echo:
        'After the shouting fades, the hardest battles ask to be lived through quietly.',
    },
    {
      title: 'Ashes and Oaths',
      icon: Flame,
      what:
        'The eagle is surrendered to the water, not in defeat, but as a solemn severing of the last chain binding Varro’s followers to imperial command.',
      why:
        'The cohort cannot become something new while still kneeling before the symbols of the old world.',
      human:
        'Varro feels the loss deeply. Lucius understands the wound between Roman and tribal blood. Aerwyna sees not an ending, but a beginning.',
      echo:
        'They do not cast it away. They return it to the land.',
    },
    {
      title: 'Aerwyna’s Burden',
      icon: Users,
      what:
        'Aerwyna is drawn into leadership not through ambition, but through grief, danger, and the trust others begin to place in her.',
      why:
        'The Ordovices need more than a warrior. They need someone who can hold memory, strategy, and spiritual legitimacy together.',
      human:
        'Aerwyna understands that to lead is to be watched by the living and measured by the dead.',
      echo:
        'A crown may glitter. A burden leaves marks no gold can hide.',
    },
    {
      title: 'Lucius Between Worlds',
      icon: Users,
      what:
        'Lucius moves between Roman discipline and tribal belonging, never fully free of either inheritance.',
      why:
        'His mixed blood makes him a bridge, but also a target for distrust from those who want simpler loyalties.',
      human:
        'His love for Aerwyna and loyalty to Varro pull him toward a future neither Rome nor the tribes have yet imagined.',
      echo:
        'Some men are not divided by two worlds. They are made responsible to both.',
    },
    {
      title: 'The Forge',
      icon: Flame,
      what:
        'Inside the Cornovii hall, factions argue over fear, pride, survival, and whether Varro’s cause will save them or destroy them.',
      why:
        'Unity cannot be declared from above. It must pass through suspicion, rivalry, and the public testing of authority.',
      human:
        'Varro sees the danger of leading people who have not chosen him. Aerwyna and Lucius must help turn doubt into something stronger than obedience.',
      echo:
        'Iron does not become a blade because it is warmed. It must endure the hammer.',
    },
    {
      title: 'The Druid’s Witness',
      icon: Landmark,
      what:
        'A druidic presence returns the struggle to older ground, reminding the people that power is not only military or political.',
      why:
        'The tribes need legitimacy rooted in land, ancestor, rite, and memory, not merely tactics borrowed from Rome.',
      human:
        'For Aerwyna, spiritual recognition does not make leadership easier. It makes refusal harder.',
      echo:
        'Before Rome named the roads, others had already named the sacred places.',
    },
    {
      title: 'Wrath of the Forgotten',
      icon: Sword,
      what:
        'Aerwyna leads a dangerous raid connected to Mona, striking toward those who have been taken, used, and almost erased.',
      why:
        'The conflict must be about more than survival of leaders. It must reach the captive, the dispossessed, and the people hidden beneath Roman supply chains.',
      human:
        'Aerwyna’s courage is not clean or easy. It carries fear, rage, compassion, and the knowledge that rescue always comes late for some.',
      echo:
        'The forgotten are not silent. They are waiting for someone to hear them.',
    },
    {
      title: 'The Mines of Mona',
      icon: BookOpen,
      what:
        'Captive labourers endure the extraction of wealth from the island, while Roman command treats suffering as a matter of supply.',
      why:
        'Mona reveals the true machinery beneath imperial order: land taken, bodies used, numbers written down, lives made invisible.',
      human:
        'For those held in bondage, freedom is not an idea. It is breath, daylight, water, and the chance to be called by one’s own name again.',
      echo:
        'A people are not free while their lost are still beneath the earth.',
    },
    {
      title: 'The Double Enemy',
      icon: Shield,
      what:
        'The allied column is trapped between threats, with enemies pressing from more than one direction and civilians at risk.',
      why:
        'The enemy understands that fear travels faster when warriors must protect the vulnerable as well as themselves.',
      human:
        'Julius becomes the iron point of sacrifice, holding the line because someone must buy time for others to live.',
      echo:
        'Some men do not fall behind. They choose the place where others may pass.',
    },
    {
      title: 'The Parley',
      icon: Users,
      what:
        'Varro faces a Roman cavalry officer in a tense exchange where both men understand the danger of needless bloodshed.',
      why:
        'Not every Roman is Catena, and not every enemy wants slaughter. The scene tests whether restraint can exist inside war.',
      human:
        'Varro must hold back allies who see only threat, while proving that unity sometimes means refusing the easy rage of the moment.',
      echo:
        'A sword left sheathed can be the hardest command a leader gives.',
    },
    {
      title: 'Nestor Taken',
      icon: Users,
      what:
        'Nestor is torn from the safety of his companions and dragged into captivity, where his skill as a healer becomes the reason he is kept alive.',
      why:
        'The enemy values bodies for labour and knowledge for use. Nestor’s compassion becomes both his strength and his vulnerability.',
      human:
        'He cannot save everyone. That helplessness wounds him more deeply than fear for himself.',
      echo:
        'A healer may be captured, but every wound he sees becomes testimony.',
    },
    {
      title: 'The Boat Intercept',
      icon: Sword,
      what:
        'A movement by water becomes a moment of danger, strategy, and revelation as supplies, captives, and imperial plans converge.',
      why:
        'Control of roads is not enough. Rivers, coasts, and hidden crossings carry the true pulse of war.',
      human:
        'Varro sees that every tactical victory reveals a wider suffering. The enemy’s system is larger than one commander or one fort.',
      echo:
        'Water carries more than boats. It carries secrets, grief, and the next war.',
    },
    {
      title: 'The Lights that Remain',
      icon: Flame,
      what:
        'Blue and white lights appear low over the land, carrying the story into older spiritual territory and a sense of reckoning beyond strategy.',
      why:
        'The struggle is not only for territory. It is for memory, belonging, and whether the land itself still recognises those who defend it.',
      human:
        'The druid understands the lights not as comfort, but as witness. The past has not gone. It has been waiting beneath the living world.',
      echo:
        'The stars above watched Rome arrive. The lights below remembered what came before.',
    },
    {
      title: 'The Oath that Remains',
      icon: Shield,
      what:
        'Cornovii, Ordovices, Deceangli, and former Romans stand together in a fragile but chosen alliance.',
      why:
        'They cannot survive as scattered griefs. They must become a people of purpose without pretending their wounds are the same.',
      human:
        'No one is asked to forget. That is what makes the oath powerful. They stand together while still carrying different dead.',
      echo:
        'Not one mind. Not one blood. One stand.',
    },
    {
      title: 'The Weight of Wings',
      icon: BookOpen,
      what:
        'The story turns toward its final reckoning, where memory of the eagle, the boar, the tribes, and the lost must find a lasting shape.',
      why:
        'Symbols cannot remain untouched. They must either harden into relics or be given back to the people who live beneath them.',
      human:
        'Varro, Lucius, and Aerwyna each face the cost of carrying hope for others when none of them are unbroken.',
      echo:
        'Wings are not only for conquest. Sometimes they are what memory grows when it refuses to die.',
    },
  ],
  sonsOfTheStandard: [
    {
      title: 'The Ash That Does Not Settle',
      icon: Flame,
      what:
        `Varro and Lucius watch ash drift across the chosen ridge as a Deceangli scout reports that raiders are sorting captives by age, strength, and skill.`,
      why:
        `The enemy is not merely taking ground. It is hollowing Britannia settlement by settlement, leaving survivors and obvious trails to fracture the tribes into separate acts of grief.`,
      human:
        `The sorting carries Varro back to the arena, but Lucius steadies him and Aerwyna names the deeper horror: “They are hollowing us.”`,
      echo:
        `The ash is not the end of the fire. It is the proof that the taking continues.`,
      account: {
        overview:
          `Ash drifting over the ridge reveals that Britannia is being dismantled by policy rather than conquered by one decisive army. The coalition’s first task is to resist the trap of scattered vengeance and turn chosen ground into refuge.`,
        whatHappened:
          `By morning, the ash had not settled. Varro and Lucius stand on the ridge the morning after their oath, watching pale threads of ash drift across the valley from burning settlements too numerous to name. A young Deceangli scout arrives breathless from the alder ford with news that changes everything: the raiders did not simply kill. They sorted the captives like livestock — counting teeth, checking hands and backs, marking numbers on wax tablets — and took only the young, the strong, and the skilled. The old were left behind. The children hid under floorboards. The ash kept rising.`,
        whyItHappened:
          `The empire's strategy in Britannia has shifted from military conquest to systematic extraction. The raids are designed to hollow out the land settlement by settlement, taking the bodies that feed the mines, quarries, and roads of empire, while leaving enough survivors to spread fear and fracture tribal unity. The raiders leave obvious trails and deliberate survivors because they want the tribes to scatter in grief, chasing separate losses rather than gathering into a single force.`,
        humanPerspective:
          `Varro feels the weight of the chosen ground beneath him — not safer, not blessed, only chosen, and that makes it infinitely heavier. The news of the sorting brings back the arena: the elliptical mouth of Rome's hunger, men stripped of names before crowds cheered their dying. His hand closes into a fist. Lucius says only his name, and that is enough. Varro opens his fist. The dead do not command the living. Aerwyna's words cut through the gathering: "They are hollowing us." The softness of her voice makes the words worse.`,
        widerMeaning:
          `This moment establishes the true nature of the enemy in Sons of the Standard. The conflict is not a war of armies but a struggle against an administrative machine that views people as ore. The ash is not metaphor — it is the literal residue of a policy. The hollowing is the novel's central horror: not the violence of conquest, but the quiet machinery of extraction that teaches men to look away while others are counted, moved, used, and forgotten.`,
        storyConnection:
          `The ridge transforms in this moment from a place of defiance into a refuge. Aerwyna's single word — "Here" — turns the ground they chose for battle into the ground they will build upon. The boar-cloth is moved from the exposed crown to a sheltered fold: its challenge has become shelter. This is the hinge on which the entire novel turns, from survival to resistance, from remnant to people.`,
        landscapeAndRitual:
          `The landscape is scarred and silent. The Roman roads lie bare and watchful, grass rising between the stones, wheel-ruts filled with dirty water. The ash physically coats the characters, clinging to cloaks, hair, and wounds. The ritual of counting and marking captives — iron brands wrapped in cloth, smoke rising below the trees — contrasts with the later rituals of speaking names aloud. The land breathes grief; the ridge breathes purpose.`,
        tension:
          `The tension lies between the urge to strike back immediately and the necessity of strategic patience. Varro must resist the bait of the obvious trails, knowing that a fractured, angry response is precisely what the empire wants. Leadership here means bearing the cost of waiting while the people are being taken. The Cornovii chieftain's jaw tightens. The Deceangli leader nods. The coalition holds — barely — because one voice names the trap before the others can run into it.`,
      },
    },
    {
      title: 'The Physician in the Chain',
      icon: Users,
      what:
        `Kept alive for his medical skill, Nestor treats captives along the extraction routes and quietly memorises names, holding points, overseers, and the rhythm of the chain.`,
      why:
        `The overseers see him as a useful function rather than a man. Their blindness gives him access to the system—and lets him hear that the bristled sign has already made a convoy disappear.`,
      human:
        `Unable to stop the suffering around him, Nestor makes witness into resistance. News of the missing convoy gives him his first genuine smile in months.`,
      echo:
        `The chain keeps records of bodies. Nestor keeps the people inside them.`,
      account: {
        overview:
          `Inside captivity, Nestor becomes the coalition’s unseen witness. His usefulness protects him, but it also places him where he can learn how the extraction network moves—and where it has begun to hurt.`,
        whatHappened:
          `Nestor, captured and moved through the extraction network, is kept alive because of his medical skills. He treats the other captives along the road, tending wounds that the overseers cannot afford to let fester if the bodies are to arrive at the mines in working condition. He begins to observe the system from the inside — the routes, the holding points, the overseers' habits, the rhythm of the chain — and he starts keeping a mental record of everything he witnesses. Then, one evening beside a fire, two overseers argue, and he hears the words that change everything: another convoy is missing. Not attacked. Not delayed. Missing. The bristled sign.`,
        whyItHappened:
          `The empire requires healthy, capable bodies to maintain its extraction quotas. Nestor's medical knowledge makes him economically valuable, granting him a vantage point within the system that ordinary labourers are denied. The overseers do not see a man; they see a function. That blindness is their greatest weakness.`,
        humanPerspective:
          `Nestor hates his privilege with a quiet, precise intensity. He watches people being sorted and moved and he cannot stop it. His resistance is internal and profound: he refuses to let the names of the taken disappear, holding them in memory as a form of moral defiance. When he hears that a convoy has gone missing and that the guards were left alive — a message, not a massacre — he looks toward the darkness beyond the camp and smiles for the first time in many months. A genuine smile.`,
        widerMeaning:
          `This moment reveals the cold, bureaucratic heart of the empire's extraction machine. The true horror is not the physical labour but the systematic reduction of human beings to numbers and functions. Nestor's act of remembering names is a powerful counter-narrative to Rome's ledgers. And the news of the missing convoy shows that the system, for all its certainty, has begun to feel pain — and pain forces even the strongest creature to reveal where it is vulnerable.`,
        storyConnection:
          `Nestor's journey maps the physical and administrative infrastructure of the chain, connecting the local raids to the broader imperial strategy. His observations will eventually provide the intelligence that allows the Sons of the Standard to strike at the system's vulnerabilities. His survival is not passive endurance; it is active intelligence-gathering, the physician turned witness, the witness turned weapon.`,
        landscapeAndRitual:
          `The landscape shifts from wild hills to the scarred, industrial world of the holding pens and drove paths. The ritual of counting and sorting dominates every halt. Nestor's internal ritual of memorising names and routes is a desperate act of preservation — a man carrying the human landscape inside him while the physical landscape is stripped bare around him.`,
        tension:
          `The tension lies in Nestor's precarious position: useful enough to survive, not so dangerous as to be eliminated. He carries the weight of witnessing suffering he cannot stop, waiting for the moment when his knowledge can become action. The news of the missing convoy shifts that tension from endurance to anticipation. The chain has noticed the banner. The question is whether the banner will find him before the chain moves him beyond reach.`,
      },
    },
    {
      title: 'The Women of the Ridge',
      icon: Users,
      what:
        `A rescue at the valley holding pens succeeds, but the terrified captives panic until the women of the ridge move among them with water, blankets, touch, and calm instructions.`,
      why:
        `Warriors know how to breach a fence, not how to soothe people who can no longer distinguish armed rescuers from captors. Aerwyna planned for people as carefully as the council planned for battle.`,
      human:
        `Lucius learns that command cannot heal terror. The women show that protection can be an act of care before it becomes an act of arms.`,
      echo:
        `The first link breaks when frightened people believe the hands reaching for them.`,
      account: {
        overview:
          `The valley rescue becomes the first proof that the coalition is more than an army. Its success depends not on the swords that open the pens, but on the women who know how to lead traumatised people back toward safety.`,
        whatHappened:
          `During the first major rescue operation at the valley holding pens, the sudden attack causes panic among the captives. Lucius vaults the fence and shouts for calm. Nobody listens. Most have never seen him before, and to them every armed man looks alike. Panic spreads faster than fire. Then something unexpected happens: the women from the ridge appear — not warriors, not chiefs, but mothers, widows, grandmothers — moving among the captives, talking, touching shoulders, giving instructions, offering blankets and water and the specific authority of people who understand fear from the inside. The panic slows. Not stopped, but slowed enough.`,
        whyItHappened:
          `The captives are traumatised and view all armed men with terror. The warriors know how to fight but not how to manage grief. The women, having spent months turning the chaos of the refuge into a functioning community, possess the empathy and practical authority needed to guide the broken. Aerwyna had foreseen this. The council had planned for battle; she had planned for people.`,
        humanPerspective:
          `Lucius realises the limits of military command in a single, humbling moment. The women act not out of martial duty but out of a deep, protective instinct, demonstrating that true leadership often looks like care rather than conquest. For Aerwyna, this is validation of something she has known since the ridge: the builders and the healers are as crucial to survival as the fighters. The distinction between warrior and protector dissolves in the valley, and what replaces it is something older and more honest.`,
        widerMeaning:
          `This moment redefines the nature of the conflict. It is not simply about warriors killing guards; it is about a community reclaiming its own. The women bring the memory of the safe ridge into the valley of the pens, using familiar gestures and voices to anchor the terrified captives in a shared reality. The rescue is not a military operation. It is an act of restoration.`,
        storyConnection:
          `The successful organisation of the captives marks the first true victory for the Sons of the Standard, proving that their coalition can function under pressure. It solidifies Aerwyna's influence and demonstrates the practical application of their new identity: they are fighting to protect, not merely to destroy. The valley of the pens is the first link in the chain that breaks — and it breaks not because of swords, but because of the women who understood what the captives needed before the captives could ask for it.`,
        landscapeAndRitual:
          `The valley is a place of temporary captivity, a node in the chain — rough buildings, fences, livestock pens, ordinary enough to pass unnoticed from the road. The ritual of the rescue replaces the ritual of the sorting. Women carrying blankets and water skins move through the same ground where overseers once carried tablets and brands. The landscape is the same; its meaning has been reversed.`,
        tension:
          `The tension is acute and doubled: the immediate physical threat of Roman riders approaching, and the emotional chaos of traumatised captives who cannot yet distinguish rescue from capture. Every second lost to confusion brings the riders closer. The women's intervention is a race against time to turn a panicking crowd into a moving column — and the margin is measured not in miles but in heartbeats.`,
      },
    },
    {
      title: 'The Road Bought with Time',
      icon: Shield,
      what:
        `Roman riders appear before the rescued captives are ready to move. Varro, Lucius, and the warriors form a line across the western road to buy the minutes needed for the vulnerable to reach the hills.`,
      why:
        `The chain has learned to answer disruption quickly. The rescue will become another captivity unless someone stands in the gap without turning the delay into a glorious suicide.`,
      human:
        `Varro and Lucius fight as equals, not commander and subordinate. They hold because every heartbeat behind them is carrying a child, a wound, or a frightened survivor toward safety.`,
      echo:
        `They do not hold the road to win it. They hold it so others may leave it behind.`,
      account: {
        overview:
          `Freedom must be purchased in time. On a road built to carry Roman power, the coalition creates a rearguard whose purpose is not conquest or sacrifice, but the safe passage of the rescued.`,
        whatHappened:
          `As Roman riders appear on the western road — forty, perhaps more — the valley still boils with confusion. Freed captives search for family. Children are being counted. The wounded are being bandaged. Nothing is ready to move. Varro and Lucius look at one another and understand what must happen. Someone must hold the road. Varro steps toward it. Lucius steps with him. The warriors form a line not to win a battle, but to buy the minutes that will carry the vulnerable into the hills. The riders come. The line holds. The captives move.`,
        whyItHappened:
          `The chain has answered faster than expected, demonstrating that the system has learned to respond to disruption. The rescue has succeeded, but the escape has not — not yet. The road must be held long enough for the freed captives to reach the eastern hills, and there is no clean way to do it. Someone must stand in the gap.`,
        humanPerspective:
          `Varro feels the old weight settle on him — the weight of choosing who lives and who dies. But this time, the choice is not about sacrifice. It is about buying time, and time is the most human of all currencies. Lucius fights beside him not as a subordinate but as an equal, and in the press of shields and shouting, the distance between Roman and half-Roman, commander and second, closes entirely. They are simply two men refusing to let the line know that fear has found a doorway.`,
        widerMeaning:
          `This moment shows that the cost of resistance is not only paid in the grand gestures of battle but in the small, grinding acts of holding ground when the odds are wrong. The road bought with time is the novel's first lesson in the price of freedom: it is not free, and those who pay it do so with their bodies, their fear, and their willingness to stand between the vulnerable and the powerful.`,
        storyConnection:
          `The rearguard action at the valley road is the moment the chain first feels the full weight of organised resistance. The riders are turned, delayed, and forced to report failure upward. That report will reach Governor Pudens. The system will respond. The war has escalated from disruption to confrontation, and neither side can pretend otherwise.`,
        landscapeAndRitual:
          `The western road — once a Roman artery of certainty — becomes the ground of resistance. The dust of the riders approaching is answered by the dust of the captives retreating eastward. The landscape holds both movements simultaneously: the old power advancing, the new people escaping. The ritual of the rearguard is ancient — men buying time with their bodies — but here it is performed not for empire, but for the living.`,
        tension:
          `The tension is relentless and physical. Forty riders against a line of exhausted warriors, with the lives of eighty freed captives depending on every second the line holds. The mathematics are brutal. The outcome is uncertain. And somewhere far to the north, Nestor walks deeper into the system, unaware that the first link has broken — and that the question now is whether the boar-cloth can survive the answer that is coming.`,
      },
    },
    {
      title: 'The Building with Lamps',
      icon: BookOpen,
      what:
        `Nestor is taken from the frontier depot into a warm, lamp-lit administration building where human beings become entries, projects, roads, walls, and labour allocations.`,
      why:
        `His literacy and medicine make him useful to an empire short of educated men. His reassignment is not mercy, but a more refined form of exploitation.`,
      human:
        `The sincere official frightens Nestor more than an obvious brute. He sees that suffering can begin with a quiet mark beside a name and a man convinced he is preserving civilisation.`,
      echo:
        `The darkest rooms of empire are sometimes the ones kept warm and well lit.`,
      account: {
        overview:
          `The building with lamps exposes the administrative heart of the chain. Here, violence is designed by ordinary men with ink-stained hands, and Nestor is placed close enough to see the whole body of the system.`,
        whatHappened:
          `Nestor is separated from the other captives at the frontier depot and escorted to a building that burns lamps long after dark. Inside, he finds no theatre of cruelty — no chains, no blood, no whip. Only warmth, lamplight, benches, and men bent over wax tablets as though the world outside could be reduced to neat lines beneath a stylus. A senior official informs him he is being reassigned to the northern administration. Not the mines. Not the quarries. Something worse: the place where people become entries, entries become projects, and projects become roads, walls, and the Wall's endless appetite for stone and men.`,
        whyItHappened:
          `Nestor's literacy and medical knowledge make him administratively valuable. The frontier is short of educated men — the empire consumes them as surely as it consumes labourers. His reassignment is not mercy; it is a more sophisticated form of use. The official who receives him is not cruel. He is sincere. He genuinely believes he is maintaining civilisation. That is the most chilling thing about him.`,
        humanPerspective:
          `Nestor sits in the warm room and understands, with a chill that moves through him more slowly than fear, that suffering does not always begin with a blade. Sometimes it begins with a mark beside a name. The official's word — useful — lands like a stone each time it is spoken. Nestor begins to hate it. He looks at the labour columns in the ledger: origin, age, skill, condition, destination, availability. There is no column for mother. None for child. None for fear, or grief, or the name someone shouted when the rope first tightened around their wrists.`,
        widerMeaning:
          `This moment exposes the administrative heart of empire — the place where the chain is not felt but designed. The building with lamps is more dangerous than any fort because it does not look dangerous. Power wearing ink stains is harder to fight than power wearing armour. The official's sincerity is the novel's darkest revelation: the machinery of erasure does not require monsters. It requires men who believe they are necessary.`,
        storyConnection:
          `Nestor's arrival at the northern administration connects the local raids to the imperial policy that drives them. He now stands at the centre of the system, able to see its full architecture. Every road, every quarry, every mine, every holding pen — all of them feed this room. And this room feeds the Wall. The chain is not a chain at all. It is a body, and Nestor has just been placed inside its beating heart.`,
        landscapeAndRitual:
          `The frontier settlement is deliberately ordinary — mud streets, warehouses, barracks, offices. The Wall is visible in the distance, a pale scar across the landscape. The ritual of the stylus scratching wax is the empire's deepest ritual: the transformation of human lives into administrative data. Nestor watches it and thinks of the druid, though he has never met him. Memory. Names. Stories. The chain preserves names too — yet somehow destroys the people attached to them.`,
        tension:
          `The tension is quiet, intellectual, and profound. Nestor is unarmed, surrounded, and holding the most dangerous secret in the province: the knowledge that the system is already feeling pressure from the south. He must appear compliant while remaining alert. The confrontation with the official is a battle fought entirely in words, where the wrong sentence could end his usefulness — and his life.`,
      },
    },
    {
      title: 'The Druid\'s Gathering',
      icon: Landmark,
      what:
        `The druid crosses ruined settlements asking survivors to speak aloud the names of those taken, refusing to let the empire’s numbers become the final account of their lives.`,
      why:
        `Ledgers make people disposable by separating names from stories. Spoken memory binds the missing back to family, land, and the living mouths that still carry them.`,
      human:
        `A grieving young man asks what use names are against swords. By saying his mother’s name, he discovers that remembrance is not helplessness but refusal.`,
      echo:
        `A person spoken by the living cannot be finished by a line of ink.`,
      account: {
        overview:
          `Against the empire’s ledgers, the druid creates a counter-ritual of spoken names. His gathering turns grief into cultural resistance and makes memory one of the coalition’s strongest forms of defence.`,
        whatHappened:
          `The druid moves through the hidden valleys and ruined settlements, asking survivors for the names of those who have been taken. He refuses to write them down. He insists that the people speak the names aloud — to the wind, to each other, to the land itself — turning memory into an act of defiance against the empire's ledgers. A young man challenges him: what use are names against Roman swords? The druid does not argue. He simply asks the young man to say his mother's name. The young man says it. The druid nods. "Now she cannot be made into a number," he says. "Not while you breathe."`,
        whyItHappened:
          `The empire's strategy relies on silence and forgetting. People turned into numbers can be discarded without consequence. The druid understands that the only way to counter this erasure is to actively preserve the identities and stories of the missing, anchoring them in the oral tradition of the tribes. Writing can be burned. Memory carried in living mouths cannot be so easily destroyed.`,
        humanPerspective:
          `The survivors are initially hesitant, broken by fear and loss. Speaking the names of the taken means acknowledging the full weight of what has been done. Yet the druid's gentle authority helps them understand that remembering is not grief prolonged — it is resistance enacted. To say a name is to refuse the ledger's verdict. To speak aloud is to insist that the person behind the name still matters, still belongs, still has a place in the world even if they cannot yet return to it.`,
        widerMeaning:
          `This moment elevates the conflict from a physical war to a spiritual one. It asserts that true survival is not just about drawing breath but about maintaining the connections that define a culture. The act of remembering is the foundation upon which the new coalition will be built — not on military victory, but on the refusal to be erased. The druid's gathering is the novel's answer to the building with lamps: against the stylus, the spoken word.`,
        storyConnection:
          `The druid's journey parallels Nestor's discovery of the ledgers. While Nestor sees how the empire erases names, the druid works to preserve them. These two acts of resistance — one inside the system, one outside it — are the twin pillars of the novel's moral architecture. The names the druid carries through the hidden valleys are the same names that will eventually be spoken at the shore when the people gather to remember what they fought for.`,
        landscapeAndRitual:
          `The landscape is vast, ancient, and silent — a witness to suffering that predates Rome and will outlast it. The ritual of speaking names around the fire is a powerful counter-ritual to the scratching of the Roman stylus. The land is invoked as the keeper of memory, ensuring that the taken are never truly gone. The wind carries the names outward, and the hills hold them.`,
        tension:
          `The tension is quiet but profound: the struggle against despair, and the temptation to surrender to the silence. The druid must convince broken people to hold onto the pain of memory, knowing that it is the only thing that will keep their identity alive in the face of overwhelming power. The young man's challenge is not hostile — it is the voice of a people on the edge of giving up. The druid's answer must be enough to pull them back.`,
      },
    },
    {
      title: 'Aerwyna Raises the Standard',
      icon: Shield,
      what:
        `When the Boar dips during the headland battle, Aerwyna catches the blood-slick haft before it touches earth and raises the standard above the wavering coalition.`,
      why:
        `The Roman wedge attacks the symbol deliberately, knowing the line may break if the visible proof of unity falls. Aerwyna moves because the standard must belong to whoever can carry it.`,
      human:
        `Ash-streaked and wounded, she lifts not for glory but for the names, the children, and the people who remain. The real weight of leadership is visible in her body.`,
      echo:
        `The Boar becomes the people’s standard when Aerwyna refuses to let it fall.`,
      account: {
        overview:
          `Aerwyna raising the standard is the central image of the novel and the turning point of the trilogy. The Boar passes beyond Varro and Roman memory into the hands of the people it has come to serve.`,
        whatHappened:
          `In the climactic battle on the headland, the warrior carrying the boar-cloth takes a javelin below the ribs. He staggers, tries to keep the haft upright, and falls to one knee. The standard dips. A sound moves through the coalition line — not panic, but the beginning of it. Aerwyna reaches the haft before it touches earth. She catches it with both hands, the weight nearly driving her down. For a heartbeat the cloth wraps around her shoulder, the painted beast hidden by wind and blood. Then she plants her feet, lifts, and raises the Boar high above the press. Her face is streaked with ash. Blood runs from a cut at her temple. The torc at her throat is dull beneath grime. She looks magnificent. She is not untouched. Because she is not.`,
        whyItHappened:
          `The Roman wedge has driven at the standard deliberately — not to kill one warrior, but to break the symbol that holds the coalition together. They understand that the Boar is more than cloth; it is the visible proof that these people are one. If it falls, the line may follow. Aerwyna understands this too, which is why she moves before anyone else can reach it.`,
        humanPerspective:
          `Aerwyna does not raise the standard as a gesture of heroism. She raises it because it must be raised, and she is the one who can. The weight of it is real — physical, not symbolic — and the effort shows in every line of her body. Her cry — "For the names! For the children! For the people who remain!" — is not a battle speech. It is a statement of what they are fighting for, spoken in the only moment when everyone can hear it.`,
        widerMeaning:
          `This is the novel's central image: a woman who has carried the grief of her people, the weight of their leadership, and the cold of the torc at her throat, now holding the standard above the press of battle. It is the answer to every ledger, every brand, every wax tablet that reduced a person to a function. The Boar does not belong to Varro. It never did. It belongs to the people who carry it when the man who made it cannot.`,
        storyConnection:
          `The moment Aerwyna raises the standard is the moment the coalition becomes something more than a military alliance. The Roman wedge slows. Then stops. The line drives it back. This is the turning point of the battle — and the turning point of the trilogy. The standard has passed from Roman hands to the hands of the people it was always meant to serve.`,
        landscapeAndRitual:
          `The headland above the western ocean is the novel's final landscape: salt wind, broken rocks, the grey sea below, and the Hibernian fleet emerging from the mist. The ritual of raising the standard is ancient — armies have always needed a visible point of courage — but here it is performed not by a commander but by a woman who has earned it through grief, endurance, and the refusal to let her people be erased. The torc at her throat is the same torc that was once a chain. Now it is simply hers.`,
        tension:
          `The tension is absolute. The standard has dipped. The line is wavering. The Roman wedge is still pressing. Aerwyna has seconds to act before the beginning of panic becomes the end of the coalition. Her cry cuts through iron and shouting. The line answers — not as one tribe, not as one army, but as one wound refusing to open further. The Roman wedge stops. The battle turns.`,
      },
    },
    {
      title: 'The Sea Answers',
      icon: Landmark,
      what:
        `As Pudens presses the coalition toward the western sea, hull after hull emerges from the mist. Cormac and the Hibernian warriors come ashore to answer the warning.`,
      why:
        `The Gangani message crossed the water, and the Hibernian chiefs understood that Britannia’s hollowing would not stop at the shore. They come by choice, not command.`,
      human:
        `Exhaustion gives way to fierce joy as Aerwyna watches old kinship rise from the surf. The coalition sees that it is no longer alone.`,
      echo:
        `Rome planned to make the sea a wall. The sea remembered it was a road.`,
      account: {
        overview:
          `The Hibernian fleet reverses the geography of the battle. The western ocean, intended as the coalition’s trap, becomes the route by which Rome’s certainty is broken.`,
        whatHappened:
          `On the headland, with the Roman line pressing and no sails visible on the horizon, Varro and Aerwyna hold the coalition together through will alone. Then the shout rises from behind the lines — not alarm, but triumph. Out of the mist, riding the incoming tide, comes the Hibernian fleet. Not a dozen ships. Not a raiding party. Hull after hull breaks through the grey, dark sails bellied by a wind that has finally chosen. Curraghs skim the shallows. Longships drive toward the coves. Cormac comes first among them, soaked to the waist, axe in hand, the marked leather collar tied around his arm. They do not arrive as rescuers. They arrive as people who understood the warning.`,
        whyItHappened:
          `The Gangani watchers sent word across the western sea weeks earlier. Cormac and the Hibernian chiefs debated, argued, and ultimately chose to come — not because they were commanded, but because they understood that the chain that was hollowing Britannia would eventually reach their shores. The sea is not a barrier in this world. It is a road, and the warning travelled it.`,
        humanPerspective:
          `Varro turns to Lucius, fierce joy cutting through exhaustion. Lucius looks past him to Aerwyna, who stands with ash on her cheek and the wind in her hair, watching Hibernia climb from the surf as if the old stories had learned to carry axes. "Then let Rome hear it," she says. Varro raises his sword. The standard surges forward. The coalition charges down the slope to meet the empire. For one moment, the headland holds everything the novel has been building toward: the sea, the people, the standard, and the refusal to be erased.`,
        widerMeaning:
          `The arrival of the Hibernian fleet is the novel's answer to the empire's certainty. Pudens had planned to crush the coalition against the sea, turning its greatest vulnerability into a killing ground. Instead, the sea becomes a road. The empire's geography has been reversed. The western ocean, which Rome regarded as the edge of the known world, carries the force that breaks its plan. What was meant to be a wall becomes a door.`,
        storyConnection:
          `The sea answering is the culmination of every choice made since the first warning fire was lit on the Gangani coast. It connects the local resistance in the hills of Britannia to the wider world beyond Rome's reach, demonstrating that the empire's power, however vast, is not universal. There are places and peoples it cannot fully control — and those places can choose to act.`,
        landscapeAndRitual:
          `The western sea is the novel's final and most powerful landscape. It has been present throughout — as threat, as boundary, as the direction Nestor was being moved — and now it delivers its answer. The ritual of the fleet's arrival is ancient: warriors leaping into surf, shields held high, voices rising with the crash of water. It is not Roman order. It is something older and no less terrible. The sea remembers what Rome has tried to forget.`,
        tension:
          `The tension of the battle pivots in a single moment. The horizon is no longer empty. The Roman cavalry, already disordered by Gangani slingers, suddenly finds enemies climbing behind and below them. A shock runs through the Roman line. Pudens sees his plan vanish from his face. For the first time, the governor has no immediate answer. The sea has ceased to be a wall. It has become a road.`,
      },
    },
    {
      title: 'The Silver Betrayal',
      icon: Sword,
      what:
        `A trusted member of the coalition is exposed as Didrugaros’s informant after a secure rescue route is compromised and the captives are moved before help arrives.`,
      why:
        `Didrugaros understands that fear, hunger, and silver can reach places soldiers cannot. The betrayal costs less than a horse, which makes its damage more bitter.`,
      human:
        `Varro’s stillness, Lucius’s silence, and Aerwyna’s grief frame the hardest question yet: how should a people seeking justice deal with one of their own who broke under fear?`,
      echo:
        `The silver buys one secret, but the judgement of it will reveal an entire people.`,
      account: {
        overview:
          `The betrayal tests the coalition from within. Its response must protect the resistance without adopting the merciless logic of the system it opposes.`,
        whatHappened:
          `In the aftermath of the valley rescue, a trusted member of the coalition is revealed to have been passing information to Didrugaros — not for ideology, but for silver. The betrayal is discovered when a route the coalition believed secure is found compromised, and captives who should have been freed are instead moved to a new holding point before the rescue party arrives. The moment of discovery is quiet and devastating: a name, a face, a man who ate at the same fire and carried the same grief, and who chose silver over the people he claimed to serve.`,
        whyItHappened:
          `Didrugaros understands that the coalition's greatest vulnerability is not military but human. A people under pressure will always contain those who are desperate enough to sell what they know. The silver is not a large sum — that is the cruelest detail. The betrayal costs less than a horse.`,
        humanPerspective:
          `Varro does not rage. He goes very still, which is worse. Lucius watches him and says nothing. Aerwyna is the one who speaks, and what she says is not condemnation but grief: "He was afraid. Fear is not an excuse. But it is a truth." The coalition must decide what to do with a man who betrayed them not out of hatred but out of hunger and terror. The decision they make will define what kind of people they are becoming.`,
        widerMeaning:
          `The silver betrayal is the novel's reminder that resistance is not pure. It is made of human beings, and human beings break. The coalition cannot afford to be naive about this, but it also cannot afford to become the thing it is fighting against — a system that punishes weakness with death. How they respond to the betrayal is as important as how they respond to the enemy.`,
        storyConnection:
          `The betrayal forces the coalition to tighten its internal structures, to think more carefully about who knows what and why. It also deepens the portrait of Didrugaros as an enemy who fights with silver as readily as with iron — a man who understands that the chain is maintained not only by force but by the corruption of those who might otherwise resist it.`,
        landscapeAndRitual:
          `The empty holding point — the pens that should have contained the freed captives but do not — is the landscape of this moment. The absence of people is its own kind of horror. The ritual of discovery is quiet: tracks read, timings compared, a name spoken. The memory of the fire they shared with the betrayer will not leave easily.`,
        tension:
          `The tension is moral rather than physical. The coalition has the man. They have the evidence. What they do next will either strengthen or fracture the bonds they have spent months building. The choice between justice and vengeance, between example and mercy, is the hardest choice the coalition has yet faced — and it must be made quickly, before the next move.`,
      },
    },
    {
      title: 'The Breaking of the Chain',
      icon: Flame,
      what:
        `The coalition strikes the extraction network in a coordinated breaking: gates are opened, guards overwhelmed, routes severed, and captives led out into the morning light.`,
      why:
        `Every earlier choice—refuge, memory, intelligence, Hibernian alliance, and the road held with blood—has created a narrow chance to interrupt the hollowing.`,
      human:
        `Varro feels no clean triumph, only a debt partly repaid as a silent child is carried free, an old man touches the earth, and a young man remembers the sky.`,
      echo:
        `The chain breaks first in iron, then in the silence of those who walk free.`,
      account: {
        overview:
          `The liberation of the pens is the novel’s moral climax. It cannot undo the taking or end Rome, but it proves that those marked for disappearance are alive, present, and capable of returning home.`,
        whatHappened:
          `The simultaneous strikes against the chain's key nodes — the valley pens, the coastal holding points, the drove-path routes — succeed. Freed captives emerge into morning light, blinking, unsteady, some weeping because they had not expected to see it. Among them are people who have been held for months, people who had given up on being found, people who had begun to believe that the world outside the pens had forgotten them. The gates open. The chain breaks. Not everywhere. Not permanently. But here, now, on this morning, it breaks.`,
        whyItHappened:
          `The coalition has spent weeks mapping the chain's routes, timing its movements, and identifying the narrow places where it is most vulnerable. The simultaneous strikes are designed to prevent the system from moving captives out of reach while one node is being attacked. It is the most complex operation the Sons of the Standard have attempted, and it requires every tribe, every ally, and every piece of intelligence Nestor gathered from inside the system.`,
        humanPerspective:
          `Varro watches the captives come out of the pens and feels something he cannot immediately name. It is not triumph — triumph is too clean. It is closer to the feeling of a debt being partially repaid: not enough, never enough, but something. A woman walks out carrying a child who makes no sound. An old man falls to his knees in the mud and presses his hands into the earth as if confirming it is real. A young man looks at the sky for a long time before he can speak.`,
        widerMeaning:
          `The breaking of the chain is the novel's moral climax — the moment when the abstract resistance becomes concrete liberation. It does not end the empire. It does not undo the years of taking. But it demonstrates that the system is not invulnerable, that the hollowing can be interrupted, that the people who were meant to disappear have not disappeared. They are here. They are alive. They are going home.`,
        storyConnection:
          `The breaking of the chain is the direct consequence of every choice made since the ridge: the refuge built, the names remembered, the Hibernians warned, the rearguard held, the ledgers read. It is the moment when all the threads of the novel's resistance come together in a single act. The coalition has not won the war. But it has proved that the war can be fought.`,
        landscapeAndRitual:
          `The pens are ordinary places — rough timber, mud, fences — and their ordinariness is part of their horror. The ritual of the gates opening is simple and devastating. People walk out into the same landscape they were taken from, and the landscape receives them. The morning light is the same light that fell on the ridge, on the ash, on the druid's fire. It has waited.`,
        tension:
          `The tension of the breaking is not the tension of battle but the tension of fragility: will the freed captives be able to move quickly enough? Will the chain respond before they reach safety? Will the coalition hold together long enough to see the operation through? Every freed person is a victory. Every delay is a risk. The margin between liberation and recapture is measured in minutes and miles.`,
      },
    },
    {
      title: 'Varro and Nestor at the Sea\'s Edge',
      icon: Users,
      what:
        `On the cliff above the sea, Varro and Nestor speak honestly about Deva, Julius, Catena, survival, and the separate futures awaiting them. Nestor gives Varro his bronze surgical hook.`,
      why:
        `Varro will leave for Hibernia so Lucius and Aerwyna can lead without his Roman shadow. Nestor remains to teach an Ordovices apprentice and root healing in the land they saved.`,
      human:
        `Two men shaped by violence choose not to become it. The hook that once closed Varro’s wounds becomes their quiet emblem of a legacy made for healing rather than harm.`,
      echo:
        `Not as debt. Not as farewell. As reminder.`,
      account: {
        overview:
          `The final conversation between Varro and Nestor closes the bond begun in Deva. Their parting reveals that the enduring legacy of the trilogy is not the weapon, but the small instrument made to close what violence has opened.`,
        whatHappened:
          `The night before the departure, Varro finds Nestor sitting alone at the cliff's edge above the western sea. They talk for a long time — about Deva, about Julius, about the choices each man made and the ones they could not make. Nestor confesses that he kept Catena alive when he could have let him die, and that he is not certain whether this was principle or cowardice. Varro tells him it was neither: it was the refusal to let Catena vanish into a convenient death, to ensure that his ending belonged to those who had paid for his life of comfort. Then Nestor draws a small bronze surgical hook from his cloak — old, worn smooth by years of use — and folds Varro's fingers around it. "Not as debt," he says. "Not as farewell. As reminder."`,
        whyItHappened:
          `Varro is leaving for Hibernia in the morning. He has understood, finally, that his presence — the armour, the voice, the Roman who gave orders when death came near — will prevent Lucius and Aerwyna from becoming what the people need them to become. He must leave not because he has failed, but because he has succeeded well enough that his absence is now the greater gift. Nestor stays because he has taken an apprentice, a quick-handed Ordovices boy who knows the plants of his own hills better than any Greek physician ever will.`,
        humanPerspective:
          `This is the novel's most intimate scene. Two men who have survived the same empire, the same fortress, the same years of being useful to powers that did not see them as human, sitting above the sea and telling each other the truth. Nestor's confession about Catena is the hardest thing he has said in three books. Varro's response — "You preserved him so the people he had broken could stand before him" — is the most generous thing Varro has ever said. The bronze hook passes between them as something more than an object: it is the only piece of Rome Nestor owns that never learned cruelty.`,
        widerMeaning:
          `The scene at the sea's edge is the novel's answer to the question it has been asking since the first page: what remains when the old banners fall? What remains is this: two men who were shaped by violence and chose, repeatedly, not to become it. The hook that stitched Varro's wounds in Deva has stitched others since — more than Nestor can count. It is smaller than a dagger, smaller than a buckle, shaped not to wound but to close what has been opened. That is the legacy of Sons of the Standard.`,
        storyConnection:
          `The conversation between Varro and Nestor closes the arc that began in Blood and Honour — the Roman soldier and the Greek physician, the man who survived the arena and the man who kept him alive to survive it. Their parting is not an ending but a continuation: Nestor stays to teach, Varro goes to begin again, and the people they have helped to build will carry on without either of them needing to stand at the front.`,
        landscapeAndRitual:
          `The cliff above the western sea is the novel's final intimate landscape. The sea breathes below them, patient and indifferent to human grief. The ritual of the gift — the bronze hook passed from one hand to another — is the quietest ritual in the book, and the most lasting. The hook is older than some of Varro's strategies and more reliable than most of them. It will go west with him. It will not be forgotten.`,
        tension:
          `The tension here is not physical but existential: the tension of two people who have relied on each other for survival now choosing to part. Varro asks Nestor to come with him. Nestor says he must stay. Neither answer is easy. The dramatic weight comes from the knowledge that this is a real goodbye — not a temporary separation, but the end of the world they built together and the beginning of the worlds they will build apart.`,
      },
    },
    {
      title: 'The Departure',
      icon: Landmark,
      what:
        `Varro enters the western shallows in his Roman armour for the last time, unfastens it before the gathered people, and gives it to the sea before boarding for Hibernia.`,
      why:
        `He cannot lay down command while still wearing its visible shape. Leaving allows Lucius and Aerwyna to become the leaders the people need and gives Varro a life beyond being Rome’s soldier.`,
      human:
        `Aerwyna asks him not to cast the armour away in anger, but to give it properly. He carries west a ridge-stone and Nestor’s bronze hook: land and healing.`,
      echo:
        `The sea takes the armour. The man who wore it continues west.`,
      account: {
        overview:
          `Varro’s departure completes the trilogy’s transformation. He does not reject his past; he acknowledges that it has finished its work, entrusts the standard to the people, and steps toward an uncertain life in Hibernia.`,
        whatHappened:
          `On the morning of the departure, Varro dresses in his old Roman armour for the last time — not with parade polish, but enough. He walks into the shallows of the western sea. The beach is crowded with embraces, with children running on the shingle, with freed captives letting the cold water run over their rope-scarred wrists. Lucius and Aerwyna come down the beach together. A young Hibernian woman at the prow of the nearest curragh watches Varro enter the shallows and asks if this is a Roman way of boarding a boat. He says no. She says it looked inefficient. Laughter moves through those close enough to hear. Varro reaches for the buckle at his shoulder. He unfastens it. The sound is slight. Yet it seems to him that half his life loosens with it. He gives the armour to the sea.`,
        whyItHappened:
          `Varro has understood that if he remains, some will always turn to him before they turn to Lucius and Aerwyna. The armour is not just protection; it is the visible form of Roman command, the thing that tells the world what he was before he opens his mouth. He cannot lay down command while still wearing its shape. The sea takes what is given. It does not always return it. That is why he chose the sea.`,
        humanPerspective:
          `The moment is witnessed by the whole beach, not because anyone has commanded silence, but because people notice. Conversations thin. Children slow. The Hibernian crews turn from their loading. Old soldiers are drawn by the sight of old iron about to meet an older judgement. Aerwyna places her hand against Varro's armoured chest and says: "This saved you once. And imprisoned you. Then do not throw it away in anger. Give it properly." He does. The stone from the ridge is in one hand, the bronze hook in the other. Land and healing. The life he is leaving, and the life he has been given back.`,
        widerMeaning:
          `The departure is the novel's final image of transformation. Varro does not walk away from the people he has fought beside. He walks toward the next thing he must become. The armour given to the sea is not a rejection of what he was; it is an acknowledgement that what he was has done its work. The standard does not need him to carry it. It never did. It needed him to understand that — and now he does.`,
        storyConnection:
          `The departure closes the trilogy's central arc: the Roman soldier who lost his empire and found something worth fighting for. Lucius and Aerwyna stand on the beach and watch him go, and they are exactly as they should be — not made safe by peace, but made ready for what peace will demand. The boar-cloth snaps in the wind above the ridge. Men and women move beneath it without looking up every moment. A sign becomes truly alive only when people no longer stare at it in wonder, but live under it as if it has always belonged.`,
        landscapeAndRitual:
          `The western sea receives the armour as it receives everything: without ceremony, without judgement, with the patience of something that has been here longer than Rome and will be here long after. The ritual of the giving is witnessed and remembered. The beach holds the sound of it — the slight sound of a buckle unfastened, a life loosened — long after the Hibernian vessels have caught the wind and the coast of Britannia has thinned to a dark line on the horizon. The stone from the ridge goes west in Varro's closed hand. The land travels with him.`,
        tension:
          `The tension of the departure is the tension of all true endings: the knowledge that the world being left behind is real, and the world being entered is uncertain. Varro does not know what Hibernia holds. He does not know who he will be when no one is waiting for the Roman to speak. That is the most honest fear in the novel — not battle, not death, but the open question of a life beyond being needed. He turns toward it anyway. That, finally, is what the standard was always for.`,
      },
    },
  ]
};

function makeSlug(title) {
  return String(title)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function buildReportId(bookKey, title) {
  return `${bookKey}__${makeSlug(title)}`;
}

function getBookMeta(bookKey) {
  if (bookKey === 'bloodAndHonour') {
    return {
      title: 'Blood and Honour',
      subtitle: 'A broken centurion. A corrupted empire. A standard reborn.',
      tone: 'Book I',
    };
  }

  if (bookKey === 'shadowsOfTheEagle') {
    return {
      title: 'Shadows of the Eagle',
      subtitle: 'A fractured land. Tribal oaths. The struggle beyond Rome.',
      tone: 'Book II',
    };
  }

  return {
    title: 'Sons of the Standard',
    subtitle: 'A hollowed land. A western alliance. A people who refuse erasure.',
    tone: 'Book III',
  };
}

function getAllReportEntries() {
  return Object.entries(sampleMoments).flatMap(([bookKey, moments]) =>
    moments.map((moment, index) => ({
      bookKey,
      moment,
      index,
      total: moments.length,
      id: buildReportId(bookKey, moment.title),
    }))
  );
}

function findReportEntry(reportId) {
  return getAllReportEntries().find((entry) => entry.id === reportId);
}

function buildReportSections(entry) {
  const { bookKey, moment, index, total } = entry;
  const meta = getBookMeta(bookKey);
  const account = moment.account || {};

  const defaultStoryConnection =
    bookKey === 'bloodAndHonour'
      ? 'Within Blood and Honour, this moment helps transform Varro’s story from punishment and survival into moral resistance. It also prepares the ground for the later shift from Roman identity toward a more complicated allegiance built from comradeship, local alliance, and hard-won trust.'
      : bookKey === 'shadowsOfTheEagle'
        ? 'Within Shadows of the Eagle, this moment belongs to a wider movement away from simple rebellion and toward fragile unity. The tribes, former Roman soldiers, healers, leaders, and captives are all drawn into a story where survival depends on trust that has not yet fully healed.'
        : 'Within Sons of the Standard, this moment carries the trilogy into its final movement: the defence of a people against erasure, the breaking of the extraction chain, and the discovery that survival may require a new homeland beyond the western sea.';

  const defaultLandscapeAndRitual =
    bookKey === 'bloodAndHonour'
      ? 'The landscape around Deva, the roads, the arena, and the hidden paths beyond the fortress all carry the pressure of occupation. Ritual here is often distorted by Rome into spectacle; the deeper counter-ritual is found in mercy, oath, endurance, and the refusal to let power define honour.'
      : bookKey === 'shadowsOfTheEagle'
        ? 'The land is not background. It is witness. Hills, water, halls, mines, roads, and sacred places become part of the moral architecture of the story. Ritual gives public shape to private grief, allowing broken loyalties to be remade in the sight of the living and the dead.'
        : 'The land, the ridge, the coast, and the western sea become witnesses to the struggle. Ritual is carried through names spoken aloud, standards raised without the Eagle, and the quiet work of turning scattered survivors into a people.';

  return {
    heading: moment.title,
    subheading: `${meta.title} — Moment ${index + 1} of ${total}`,
    overview:
      account.overview ||
      `${moment.what} ${moment.why} Seen within ${meta.title}, this moment is not simply an incident in the plot; it is a hinge-point where pressure, memory, and choice begin to reshape the people involved.`,
    whatHappened: account.whatHappened || moment.what,
    whyItHappened: account.whyItHappened || moment.why,
    humanPerspective:
      account.humanPerspective ||
      `${moment.human} The scene matters because it keeps the conflict grounded in human consequence rather than spectacle alone. What changes here is not only the movement of armies or the turn of events, but the inner weight carried by those who survive it.`,
    widerMeaning:
      account.widerMeaning ||
      'This moment deepens the storyworld by connecting personal fate to the larger struggle between empire, tribe, memory, and land. It gives the reader a way to understand why the conflict cannot be reduced to Rome against Britain, or soldier against warrior. Every choice carries older wounds, hidden loyalties, and the possibility of a different future.',
    storyConnection: account.storyConnection || defaultStoryConnection,
    landscapeAndRitual: account.landscapeAndRitual || defaultLandscapeAndRitual,
    tension:
      account.tension ||
      'The tension in this moment comes from the fact that no choice is clean. Action may save some and endanger others. Mercy may look like weakness. Defiance may invite retaliation. Leadership means carrying the cost of decisions that ordinary people will have to live through.',
    echo: moment.echo,
  };
}

function SampleMomentCard({ bookKey, bookTitle, subtitle, badge, moment, momentNumber, totalMoments, onChange, onOpenReport }) {
  const Icon = moment.icon;

  return (
    <article className="rounded-lg border border-amber-500/40 bg-stone-900/70 p-5 shadow-lg flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-xl font-bold text-amber-200 mb-1">{bookTitle}</h3>
          <p className="text-sm text-stone-300 leading-relaxed">{subtitle}</p>
          <p className="mt-2 text-xs uppercase tracking-wide text-amber-300/80">
            Moment {momentNumber} of {totalMoments}
          </p>
        </div>

        {badge && (
          <span className="shrink-0 rounded-full border border-amber-500/50 bg-stone-800 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-200">
            {badge}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-amber-400" />
        <h4 className="text-lg font-semibold text-amber-100">{moment.title}</h4>
      </div>

      <div className="space-y-4 text-sm leading-relaxed text-stone-200">
        <div>
          <p className="font-semibold text-amber-200 mb-1">What happened</p>
          <p>{moment.what}</p>
        </div>

        <div>
          <p className="font-semibold text-amber-200 mb-1">Why it happened</p>
          <p>{moment.why}</p>
        </div>

        <div>
          <p className="font-semibold text-amber-200 mb-1">Human perspective</p>
          <p>{moment.human}</p>
        </div>

        <blockquote className="border-l-4 border-amber-600 bg-stone-800/80 px-4 py-3 italic text-amber-100">
          {moment.echo}
        </blockquote>
      </div>

      <div className="mt-auto pt-5 grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onChange(bookKey)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
        >
          <RefreshCw className="h-4 w-4" />
          Show next moment
        </button>

        <button
          type="button"
          onClick={() => onOpenReport(buildReportId(bookKey, moment.title))}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-500/60 bg-stone-800 px-4 py-2 text-sm font-semibold text-amber-100 transition hover:bg-stone-700"
        >
          <BookOpen className="h-4 w-4" />
          Read the Account
        </button>
      </div>
    </article>
  );
}

function BookMomentReport({ reportId, onBack, onOpenReport }) {
  const entries = getAllReportEntries();
  const entry = findReportEntry(reportId) || entries[0];
  const currentFlatIndex = entries.findIndex((item) => item.id === entry.id);
  const previousEntry = entries[(currentFlatIndex - 1 + entries.length) % entries.length];
  const nextEntry = entries[(currentFlatIndex + 1) % entries.length];
  const meta = getBookMeta(entry.bookKey);
  const report = buildReportSections(entry);
  const Icon = entry.moment.icon;

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100 py-10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onBack}
          className="mb-6 inline-flex items-center gap-2 rounded-lg border border-amber-500/50 bg-stone-800 px-4 py-2 text-sm font-semibold text-amber-100 transition hover:bg-stone-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to the books
        </button>

        <article className="rounded-xl border border-amber-600 bg-gradient-to-b from-stone-800 to-stone-900 p-6 md:p-8 shadow-xl">
          <div className="mb-6 border-b border-amber-500/30 pb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-amber-300/80">
              {meta.tone}
            </p>
            <div className="flex items-start gap-3">
              <Icon className="mt-1 h-7 w-7 shrink-0 text-amber-400" />
              <div>
                <h1 className="text-3xl font-bold text-amber-100 md:text-4xl">
                  {report.heading}
                </h1>
                <p className="mt-2 text-sm text-stone-300">{report.subheading}</p>
              </div>
            </div>
            <p className="mt-5 text-base leading-relaxed text-stone-200">
              {report.overview}
            </p>
          </div>

          <div className="space-y-6">
            <ReportSection title="What happened" text={report.whatHappened} />
            <ReportSection title="Why it happened" text={report.whyItHappened} />
            <ReportSection title="Human perspective" text={report.humanPerspective} />
            <ReportSection title="Wider meaning" text={report.widerMeaning} />
            <ReportSection title="Storyworld connection" text={report.storyConnection} />
            <ReportSection title="Landscape, ritual, and memory" text={report.landscapeAndRitual} />
            <ReportSection title="Dramatic tension" text={report.tension} />

            <blockquote className="rounded-lg border-l-4 border-amber-600 bg-stone-800/80 px-5 py-4 text-lg italic leading-relaxed text-amber-100">
              {report.echo}
              <span className="mt-3 block text-sm not-italic text-amber-200/80">
                — remembered in the telling
              </span>
            </blockquote>
          </div>
        </article>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onOpenReport(previousEntry.id)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-amber-500/50 bg-stone-800 px-4 py-3 text-sm font-semibold text-amber-100 transition hover:bg-stone-700"
          >
            <ChevronLeft className="h-4 w-4" />
            Earlier account
          </button>

          <button
            type="button"
            onClick={() => onOpenReport(nextEntry.id)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-700"
          >
            Later account
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportSection({ title, text }) {
  return (
    <section>
      <h2 className="mb-2 text-xl font-bold text-amber-200">{title}</h2>
      <p className="text-sm leading-relaxed text-stone-200 md:text-base">{text}</p>
    </section>
  );
}

function BookFlipCard({
  id,
  title,
  backTitle,
  frontLine,
  image,
  amazonUrl,
  buttonLabel = 'Kindle Edition',
  icon: Icon,
  iconClassName,
  activeBookCard,
  setActiveBookCard,
  children,
}) {
  const isActive = activeBookCard === id;
  // The Sons link is deliberately fixed here so an empty prop can never
  // make the published book display as "Coming Soon" again.
  const resolvedAmazonUrl =
    id === 'sons' ? SONS_OF_THE_STANDARD_AMAZON_URL : amazonUrl;

  return (
    <article
      className="group h-[620px] cursor-pointer [perspective:2000px]"
      onClick={() => setActiveBookCard(isActive ? null : id)}
    >
      <div
        className={`relative h-full rounded-xl transition-transform duration-700 [transform-style:preserve-3d] ${
          isActive ? '[transform:rotateY(180deg)]' : ''
        }`}
        style={{
          WebkitTransformStyle: 'preserve-3d',
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          className="absolute inset-0 overflow-hidden rounded-xl border border-amber-600 shadow-2xl [backface-visibility:hidden]"
          style={{
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
          }}
        >
          <img
            src={image}
            alt={backTitle || title || 'Book cover'}
            className="absolute inset-0 h-full w-full object-cover object-top brightness-125 transition duration-700 group-hover:scale-105 group-hover:brightness-125"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

          <div className="relative z-10 flex h-full flex-col justify-end p-6 md:p-8">
            {title && (
              <h2 className="mb-3 text-3xl font-extrabold uppercase leading-tight text-white md:text-4xl">
                {title}
              </h2>
            )}

            {frontLine && (
              <p className="mb-6 max-w-md text-base leading-relaxed text-stone-200 md:text-lg">
                {frontLine}
              </p>
            )}

            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200/80">
              Tap to read more
            </p>
          </div>
        </div>

        <div
          className="absolute inset-0 overflow-hidden rounded-xl border border-amber-600 bg-gradient-to-br from-stone-900 via-stone-900 to-amber-950 p-6 text-stone-200 shadow-2xl [backface-visibility:hidden] [transform:rotateY(180deg)] md:p-8"
          style={{
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg) translateZ(0)',
          }}
        >
          <div className="flex h-full flex-col justify-between">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <Icon className={`h-6 w-6 ${iconClassName || ''}`} />
                <h2 className="text-2xl font-bold uppercase text-amber-100 md:text-3xl">
                  {backTitle || title}
                </h2>
              </div>

              <div className="space-y-4 text-sm leading-relaxed text-stone-200 md:text-base">
                {children}
              </div>
            </div>

            <div className="mt-auto flex flex-wrap items-center gap-3 pt-5">
              {resolvedAmazonUrl ? (
                <a
                  href={resolvedAmazonUrl}
                  data-amazon-link={id === 'sons' ? 'B0GX2ZYLMY' : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 sm:w-auto"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {buttonLabel}
                </a>
              ) : (
                <span className="inline-flex w-full items-center justify-center rounded-lg border border-amber-500/60 bg-stone-800 px-4 py-2 text-sm font-semibold text-amber-100 sm:w-auto">
                  Coming Soon
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

const Books = () => {
  const [selectedMomentIndexes, setSelectedMomentIndexes] = useState({
    bloodAndHonour: 0,
    shadowsOfTheEagle: 0,
    sonsOfTheStandard: 0,
  });
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [activeBookCard, setActiveBookCard] = useState(null);

  const handleChangeMoment = (bookKey) => {
    setSelectedMomentIndexes((current) => ({
      ...current,
      [bookKey]: (current[bookKey] + 1) % sampleMoments[bookKey].length,
    }));
  };

  if (selectedReportId) {
    return (
      <BookMomentReport
        reportId={selectedReportId}
        onBack={() => setSelectedReportId(null)}
        onOpenReport={setSelectedReportId}
      />
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <BookOpen className="h-12 w-12 text-amber-400" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-amber-100 mb-3">
            The Novels
          </h1>

          <p className="text-stone-300 max-w-2xl mx-auto">
            Explore the historical fiction works of Martin Antrobus, set in the world of Roman Britain.
          </p>
        </div>

        <section className="mb-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <BookFlipCard
            id="blood"
            title=""
            backTitle="Blood and Honour"
            frontLine=""
            image="/images/blood-and-honour.png"
            amazonUrl="https://www.amazon.co.uk/Blood-Honour-Sons-Standard-Book-ebook/dp/B0FCDPQSZY/ref=sr_1_3?crid=3K516KNBJT4R&keywords=blood+and+honour&qid=1751817522&sprefix=blood+and+honour%2Caps%2C74&sr=8-3"
            icon={Sword}
            iconClassName="text-red-400"
            activeBookCard={activeBookCard}
            setActiveBookCard={setActiveBookCard}
          >
            <p className="leading-relaxed mb-4">
              In the aftermath of civil war, the disgraced centurion Gaius Marcellus Varro is condemned to the arena beneath the rule of the corrupt procurator Paulus Catena.
            </p>

            <p className="leading-relaxed mb-4">
              Escaping captivity, Varro is drawn into rebellion beside the Cornovii and Ordovices tribes, rebuilding a fragment of the legio to defend the oppressed.
            </p>

            <p className="leading-relaxed mb-6">
              A story of honour, vengeance, loyalty, and identity in Roman Britain.
            </p>
          </BookFlipCard>

          <BookFlipCard
            id="shadows"
            title=""
            backTitle="Shadows of the Eagle"
            frontLine=""
            image="/images/shadows-of-the-eagle.png"
            amazonUrl="https://www.amazon.co.uk/dp/B0GZ8V3NNV/ref=sr_1_12?crid=2D2NGLM550C1I&dib=eyJ2IjoiMSJ9.CaPJ38l9056UPAZp9D_IZDdTng6lrbDrdiR7CB-brZNVO_jzLufGzAp-Q5i8wd-zCEREO1QwhaA30Vf9OcTegjJPIFjLgVL12JeVNTzsyc4vMQZ0Q8BnGHjvA-7BZZNYjC4mAfDRahhjQeGiy7-wphIqiut075kXOzxrbTJwFVWanG0G2aTOz68imHz0AKDc49_Sue2bdK3N8xQDII0O-3Jh1uDFNLOEc8YBeMtUTu8.aK2gP5P1D4mw58P5tFmqLHGhOD6c74p5JDuNCGkBo9Y&dib_tag=se&keywords=shadows+of+the+eagle&qid=1777731302&sprefix=shadows+of+the+eagle%2Caps%2C315&sr=8-12"
            icon={Shield}
            iconClassName="text-blue-400"
            activeBookCard={activeBookCard}
            setActiveBookCard={setActiveBookCard}
          >
            <p className="leading-relaxed mb-4">
              Across Roman Britain and the western sea, Varro and his allies pursue those taken into bondage beneath Rome’s shadow.
            </p>

            <p className="leading-relaxed mb-4">
              As Aerwyna rises among the Ordovices, old rivalries threaten the fragile unity forged through blood, sacrifice, and memory.
            </p>

            <p className="leading-relaxed mb-6">
              The struggle becomes one not only for survival, but for freedom and the meaning of the eagle itself.
            </p>
          </BookFlipCard>

          <BookFlipCard
            id="sons"
            title=""
            backTitle="Sons of the Standard"
            frontLine=""
            image="/images/sons-of-the-standard.png"
            amazonUrl={SONS_OF_THE_STANDARD_AMAZON_URL}
            buttonLabel="Buy on Amazon"
            icon={Flame}
            iconClassName="text-amber-400"
            activeBookCard={activeBookCard}
            setActiveBookCard={setActiveBookCard}
          >
            <p className="leading-relaxed mb-4">
              The final movement of the trilogy begins with Britannia hollowed by fire, raids, and the quiet violence of ledgers.
            </p>

            <p className="leading-relaxed mb-4">
              Varro, Lucius, Aerwyna, Nestor, and the peoples of the ridge must fight not merely for victory, but for memory, kinship, and the recovery of those taken.
            </p>

            <p className="leading-relaxed mb-6">
              When the land is hollowed, the people become the walls.
            </p>
          </BookFlipCard>
        </section>

        <section className="bg-gradient-to-r from-stone-800 via-stone-800 to-amber-950 rounded-lg border border-amber-600 p-6 md:p-8 mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-amber-100 mb-3">
              Step Into the Story
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <SampleMomentCard
              bookKey="bloodAndHonour"
              bookTitle="Blood and Honour"
              subtitle="A broken centurion. A corrupted empire. A standard reborn."
              moment={sampleMoments.bloodAndHonour[selectedMomentIndexes.bloodAndHonour]}
              momentNumber={selectedMomentIndexes.bloodAndHonour + 1}
              totalMoments={sampleMoments.bloodAndHonour.length}
              onChange={handleChangeMoment}
              onOpenReport={setSelectedReportId}
            />

            <SampleMomentCard
              bookKey="shadowsOfTheEagle"
              bookTitle="Shadows of the Eagle"
              subtitle="A fractured land. Tribal oaths. The struggle beyond Rome."
              moment={sampleMoments.shadowsOfTheEagle[selectedMomentIndexes.shadowsOfTheEagle]}
              momentNumber={selectedMomentIndexes.shadowsOfTheEagle + 1}
              totalMoments={sampleMoments.shadowsOfTheEagle.length}
              onChange={handleChangeMoment}
              onOpenReport={setSelectedReportId}
            />

            <SampleMomentCard
              bookKey="sonsOfTheStandard"
              bookTitle="Sons of the Standard"
              subtitle="A hollowed land. A western alliance. A people who refuse erasure."
              moment={sampleMoments.sonsOfTheStandard[selectedMomentIndexes.sonsOfTheStandard]}
              momentNumber={selectedMomentIndexes.sonsOfTheStandard + 1}
              totalMoments={sampleMoments.sonsOfTheStandard.length}
              onChange={handleChangeMoment}
              onOpenReport={setSelectedReportId}
            />
          </div>

          <div className="mt-8 text-center">
            <a
              href="/philosophy"
              className="inline-block rounded-xl bg-amber-700 px-5 py-3 text-white hover:bg-amber-800 transition"
            >
              Explore the philosophy behind the storyworld
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Books;